import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { ApplicationContext } from '../../App'
import { getClaims } from '../../services/claimsService'
import { getUseCases, postMockRequest } from '../../services/miscService'

interface ClaimsMockPanelProps {
    close: (data: object) => any
    open: boolean
}

export const ClaimsMockPanel = ({ open, close }: ClaimsMockPanelProps) => {
    const identities = useContext(ApplicationContext)?.identities ?? []
    const ownOrg = identities.find((entry: any) => entry.self)
    const nonOwnOrg = identities.find((entry) => !entry.self)
    const [messageType, setMessageType] = useState('837')
    const [sourceInputValue, setSourceInputValue] = useState('')
    const [destinationInputValue, setDestinationInputValue] = useState(nonOwnOrg?.interchangeId)
    const [claimRequests, setClaimRequests] = useState([])
    const [claimRequestInputValue, setClaimRequestInputValue] = useState('')
    const [useCaseInputValue, setUseCaseInputValue] = useState('')
    const [useCaseItems, setUseCaseItems] = useState([])

    const handleChange = (event: SelectChangeEvent) => {
        setMessageType(event.target.value as string)
        setClaimRequestInputValue('')
    }
    const handleDestinationChange = (event: SelectChangeEvent) => {
        setDestinationInputValue(event.target.value as string)
    }
    const handleSourceChange = (event: SelectChangeEvent) => {
        setSourceInputValue(event.target.value as string)
    }
    const handleClaimRequestChange = (event: SelectChangeEvent) => {
        setClaimRequestInputValue(event.target.value as string)
    }
    const closeEvent = () => {
        close({})
    }
    const handleUseCaseTypeChange = (event: SelectChangeEvent) => {
        setUseCaseInputValue(event.target.value as string)
    }
    const isComplete = () => {
        let complete =
            messageType &&
            messageType.length > 0 &&
            destinationInputValue &&
            destinationInputValue.length > 0
        if (messageType === '837') {
            complete = useCaseInputValue.length > 0
        } else {
            complete = claimRequestInputValue.length > 0
        }
        return complete
    }

    const generateAndCloseEvent = () => {
        const data = {
            claimType: messageType,
            source: sourceInputValue,
            destination: destinationInputValue,
            claimNumber: claimRequestInputValue,
            useCaseType: useCaseInputValue,
        }
        postMockRequest(data).then((response) => {
            close(response.data)
        })
    }

    const getDestinationIdentities = () => {
        const items = identities.filter((i) => !i.self)
        if (!items) return
        if (messageType === '837' || messageType === '837R') {
            return items
                .filter((r) => r.type === 'payer')
                .map((identity) => {
                    return (
                        <MenuItem value={identity.interchangeId} key={identity.interchangeId}>
                            {identity.displayName}
                        </MenuItem>
                    )
                })
        } else {
            return items
                .filter((r) => r.type === 'provider')
                .map((identity) => {
                    return (
                        <MenuItem value={identity.interchangeId} key={identity.interchangeId}>
                            {identity.displayName}
                        </MenuItem>
                    )
                })
        }
    }

    const getSourceIdentities = () => {
        const items = ownOrg?.type === 'rhiza' ? identities : identities.filter((i) => i.self)
        if (!items) return
        if (messageType === '837' || messageType === '837R') {
            return items
                .filter((r) => r.type === 'provider')
                .map((identity) => {
                    return (
                        <MenuItem value={identity.interchangeId} key={identity.interchangeId}>
                            {identity.displayName}
                        </MenuItem>
                    )
                })
        } else {
            return items
                .filter((r) => r.type === 'payer')
                .map((identity) => {
                    return (
                        <MenuItem value={identity.interchangeId} key={identity.interchangeId}>
                            {identity.displayName}
                        </MenuItem>
                    )
                })
        }
    }

    const getMessageTypeOptions = () => {
        if (!ownOrg) return
        const items = ['837', '835', '837R'] as any[]
        if (ownOrg.type === 'provider') {
            return items
                .filter((r) => r !== '835')
                .map((value) => {
                    return (
                        <MenuItem value={value} key={value}>{`${value} Claims Request`}</MenuItem>
                    )
                })
        } else if (ownOrg.type === 'payer') {
            return <MenuItem value="835">835 Claims Response</MenuItem>
        } else {
            return items.map((value, i) => {
                if (value === '835') {
                    return (
                        <MenuItem key={i} value="835">
                            835 Claims Response
                        </MenuItem>
                    )
                } else {
                    return (
                        <MenuItem value={value} key={value}>{`${value} Claims Request`}</MenuItem>
                    )
                }
            })
        }
    }

    const getUseCaseTypeOptions = () => {
        return useCaseItems.map((item: any) => {
            return (
                <MenuItem value={item.key} key={item.key}>
                    {item.value}
                </MenuItem>
            )
        })
    }

    useEffect(() => {
        getClaims(0, 10, messageType).then((response) => {
            setClaimRequests(response.data)
        })
        getUseCases().then((response) => {
            setUseCaseItems(response.data)
        })
    }, [messageType])
    return (
        <Dialog open={open} onClose={close} maxWidth="lg">
            <DialogTitle>Details</DialogTitle>
            <DialogContent>
                <Box style={{ display: 'flex', flexDirection: 'column', width: '640px' }}>
                    <InputLabel id="messageType-select-label">Message Type</InputLabel>
                    <Select
                        value={messageType}
                        labelId="messageType-select-label"
                        onChange={handleChange}
                    >
                        {getMessageTypeOptions()}
                    </Select>
                    <InputLabel id="source-select-label">Source</InputLabel>
                    <Select
                        value={sourceInputValue}
                        labelId="source-select-label"
                        style={{ marginBottom: '20px' }}
                        onChange={handleSourceChange}
                    >
                        {getSourceIdentities()}
                    </Select>
                    <InputLabel id="destination-select-label">Destination</InputLabel>
                    <Select
                        value={destinationInputValue}
                        labelId="destination-select-label"
                        style={{ marginBottom: '20px' }}
                        onChange={handleDestinationChange}
                    >
                        {getDestinationIdentities()}
                    </Select>
                    {messageType === '837' && (
                        <>
                            <InputLabel id="usecase-select-label">Use Case</InputLabel>
                            <Select
                                value={useCaseInputValue}
                                labelId="usecase-select-label"
                                onChange={handleUseCaseTypeChange}
                            >
                                {getUseCaseTypeOptions()}
                            </Select>
                        </>
                    )}
                    {messageType !== '837' && (
                        <>
                            <InputLabel id="claimRequest-select-label">Claims Request</InputLabel>
                            <Select
                                fullWidth
                                value={claimRequestInputValue}
                                labelId="claimRequest-select-label"
                                onChange={handleClaimRequestChange}
                            >
                                {claimRequests.map((request: any) => {
                                    return (
                                        <MenuItem
                                            value={request.publicData.claimNumber}
                                            key={request.publicData.claimNumber}
                                        >
                                            {request.publicData.claimNumber}
                                        </MenuItem>
                                    )
                                })}
                            </Select>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    disableElevation={true}
                    disabled={!isComplete()}
                    variant="contained"
                    style={{ minWidth: '120px' }}
                    onClick={generateAndCloseEvent}
                >
                    Generate Data
                </Button>
                <Button
                    disableElevation={true}
                    variant="contained"
                    style={{ minWidth: '120px' }}
                    onClick={closeEvent}
                >
                    close
                </Button>
            </DialogActions>
        </Dialog>
    )
}
